import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  transport,
  useTransportCollectionQuery,
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import type { ListAlertEventsRequest } from "@/types_api/backend/transport/list_alert_events_request"
import type { ListAlertEventsResponse } from "@/types_api/backend/transport/list_alert_events_response"
import type { ListAlertsRequest } from "@/types_api/backend/transport/list_alerts_request"
import type { ListAlertsResponse } from "@/types_api/backend/transport/list_alerts_response"

import { normalizeAlert, type Alert } from "../types"

type CreateAlertMutationOptions = Parameters<
  typeof useTransportMutation<"createAlert">
>[1]
type UpdateAlertMutationOptions = Parameters<
  typeof useTransportMutation<"updateAlert">
>[1]
type DeleteAlertMutationOptions = Parameters<
  typeof useTransportMutation<"deleteAlert">
>[1]
type AlertResource = ListAlertsResponse["alerts"][number]

const DEFAULT_ALERTS_PAGE_SIZE = 100

const ALERTS_LIST_REQUEST = {
  page_size: DEFAULT_ALERTS_PAGE_SIZE,
} satisfies ListAlertsRequest

interface TransportLoadingStateInput {
  data: unknown
  enabled: boolean
  error: unknown
  isLoading: boolean
}

function isAlertTransportQueryLoading({
  data,
  enabled,
  error,
  isLoading,
}: TransportLoadingStateInput): boolean {
  return isLoading || (enabled && data == null && error == null)
}

export function useAlertsListTransport(options?: {
  enabled?: boolean
  request?: ListAlertsRequest
}) {
  const enabled = options?.enabled ?? true
  const request = useMemo(
    () => ({
      ...ALERTS_LIST_REQUEST,
      ...options?.request,
    }),
    [options?.request],
  )

  const query = useTransportCollectionQuery<"listAlerts", AlertResource>({
    operation: transport.collections.listAlerts,
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
    },
  })

  const alerts = useMemo(
    () =>
      query.data?.items.flatMap((alert) => {
        const normalized = normalizeAlert(alert)
        return normalized ? [normalized] : []
      }) ?? [],
    [query.data?.items],
  )
  const lastPage = query.data?.pages.at(-1)

  return {
    ...query,
    data: query.data
      ? {
          next_page_token: lastPage?.next_page_token ?? null,
          total_count: lastPage?.total_count ?? alerts.length,
          alerts,
        }
      : undefined,
    isLoading: isAlertTransportQueryLoading({
      data: query.data,
      enabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

export function useAlertByIdTransport(
  alertId: string | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(alertId) && (options?.enabled ?? true)
  const query = useTransportQuery({
    operation: transport.operations.getAlert,
    request: { alert_id: alertId ?? "" },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
    },
  })

  const alert: Alert | undefined = useMemo(
    () => normalizeAlert(query.data?.alert),
    [query.data?.alert],
  )

  return {
    ...query,
    data: alert,
    isLoading: isAlertTransportQueryLoading({
      data: query.data,
      enabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

export function useAlertEventsTransport(
  request: ListAlertEventsRequest,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(request.alert_id) && (options?.enabled ?? true)
  return useTransportQuery({
    operation: "listAlertEvents",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
      select: (response): ListAlertEventsResponse => response,
    },
  })
}

export function useCreateAlertTransport(options?: CreateAlertMutationOptions) {
  return useTransportMutation(transport.operations.createAlert, options)
}

export function useUpdateAlertTransport(options?: UpdateAlertMutationOptions) {
  return useTransportMutation(transport.operations.updateAlert, options)
}

export function useDeleteAlertTransport(options?: DeleteAlertMutationOptions) {
  return useTransportMutation(transport.operations.deleteAlert, options)
}

export function useAlertTransportInvalidation() {
  const transportUtils = useTransportQueryUtils()

  return useCallback(
    async (alertId?: string) => {
      const invalidations = [
        transportUtils.invalidate(transport.resources.alerts),
      ]
      if (alertId) {
        invalidations.push(
          transportUtils.invalidate(transport.operations.getAlert, {
            alert_id: alertId,
          }),
        )
      }
      await Promise.all(invalidations)
    },
    [transportUtils],
  )
}
